import { CSSProperties, ReactNode, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  contentClassName?: string;
  overlayClassName?: string;
  panelStyle?: CSSProperties;
  /** Allows dropdowns/popovers inside modal content to extend beyond panel bounds */
  allowOverflow?: boolean;
  /**
   * Nested modal opened above another modal.
   * Escape closes only this layer; body scroll lock is left to the parent.
   */
  nested?: boolean;
}

const ModalPortal = ({ children }: { children: React.ReactNode }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted ? createPortal(children, document.body) : null;
};

export const Modal = ({ 
  isOpen, 
  onClose, 
  title, 
  children,
  size = 'md',
  className,
  contentClassName,
  overlayClassName,
  panelStyle,
  allowOverflow = false,
  nested = false,
}: ModalProps) => {
  
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (nested) {
        e.stopImmediatePropagation();
      }
      onClose();
    }
  }, [nested, onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const previousOverflow = document.body.style.overflow;
    if (!nested) {
      document.body.style.overflow = 'hidden';
    }
    // Nested uses capture so Escape closes this layer without closing the parent.
    document.addEventListener('keydown', handleKeyDown, nested);
    
    return () => {
      if (!nested) {
        document.body.style.overflow = previousOverflow;
      }
      document.removeEventListener('keydown', handleKeyDown, nested);
    };
  }, [isOpen, handleKeyDown, nested]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    // Portals bubble through the React tree; stop so a parent overlay
    // (e.g. ImageViewerModal) does not close on nested modal clicks.
    e.stopPropagation();
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <ModalPortal>
      <div
        className={`${styles.overlay} ${nested ? styles.overlayNested : ''} ${overlayClassName ?? ''}`.trim()}
        onClick={handleOverlayClick}
      >
        <div
          className={`modals ${styles.modal} ${styles[size]} ${allowOverflow ? styles.allowOverflow : ''} ${className ?? ''}`.trim()}
          style={panelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className={styles.header}>
            {title && <h2 className={styles.title}>
              {title}
            </h2>}
            <button className={styles.closeButton} onClick={onClose}>
              ×
            </button>
          </div>
          <div className={`${styles.content} ${contentClassName ?? ''}`.trim()}>
            {children}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
